import { useEffect } from "react";
import { Outlet, useNavigate } from "react-router";
import { useServices } from "@/app/use-services";
import { useSession } from "@/lib/auth/use-session";
import { PATHS } from "@/features/onboarding/destinations";
import { Working } from "@/ui/Working";
import { TabBar } from "./TabBar";

/**
 * The signed-in guard. Anyone who arrives here signed out is sent to the
 * root, which knows where they belong; while the session is being read the
 * person sees a wait rather than a flash of the wrong screen.
 */
export function SignedIn() {
  const { session } = useServices();
  const snapshot = useSession(session);
  const navigate = useNavigate();

  useEffect(() => {
    if (snapshot.status === "signed_out") navigate(PATHS.root, { replace: true });
  }, [snapshot.status, navigate]);

  if (snapshot.status !== "signed_in") return <Working message="One moment." />;
  return (
    <div className="min-h-app bg-paper text-ink">
      <Outlet />
    </div>
  );
}

interface ShellProps {
  /**
   * Whether a phone shows the tab bar. Tabs do; detail pages do not — they
   * are pushed over the tabs, as on the phone app. A desktop always has
   * the sidebar, because there is nothing to push over.
   */
  readonly tabsOnPhone?: boolean;
}

/** Content above the tab bar on a phone; a sidebar beside a reading column on a desktop. */
export function Shell({ tabsOnPhone = true }: ShellProps) {
  return (
    <div className="flex min-h-app flex-col md:flex-row">
      {/* Both levels are flex columns so a page can fill the height and centre itself. */}
      <div className="order-first flex flex-1 flex-col md:order-last md:flex-row md:justify-center">
        <div className="flex w-full flex-1 flex-col md:max-w-[680px] md:px-12 md:py-10">
          <Outlet />
        </div>
      </div>
      <TabBar phone={tabsOnPhone} />
    </div>
  );
}
